'use client';

import { useAssetRegistry } from '@/hooks/use-asset-registry';

import type { InvestorHoldingsResponse } from '../types/api';
import {
  needsCurrencyNames,
  type CurrencyRegistry
} from '../utils/holding-format';

interface HoldingCurrencies {
  /** Names by lowercased address, once the registry has answered. */
  registry: CurrencyRegistry | undefined;
  /**
   * The registry query, for the recovery banner and its retry. Null when
   * the response holds no currency that needs naming: a lookup that was
   * never needed is never reported, whatever its cache says.
   */
  lookup: ReturnType<typeof useAssetRegistry> | null;
  /**
   * The names were needed and could not be loaded, so those currencies
   * read as a shortened address. The view says so in plain words.
   */
  namesMissing: boolean;
}

/**
 * The names behind the strategy currency addresses in a holdings response.
 *
 * A ROWA row's `nativeCurrency`, and every `fx.rates[].currency`, is a
 * contract address. Dollar stablecoins are named by the chain's
 * `supportedTokens` with no request at all. Anything else is named by the
 * asset registry, which is loaded only when the response actually holds such
 * a currency, so a wallet of dollar vaults costs nothing here.
 *
 * Called by the view and passed down: the components that print values
 * stay free of fetching. Until the registry answers (or if it fails) those
 * currencies read as a shortened address, never the raw one, and a failure
 * is handed back so the view can report it and retry it.
 *
 * Retries: `useAssetRegistry` takes no retry option, so this inherits the
 * query client's default.
 */
export function useHoldingCurrencies(
  holdings: InvestorHoldingsResponse | undefined
): HoldingCurrencies {
  const needed = holdings != null && needsCurrencyNames(holdings);
  const registry = useAssetRegistry(holdings?.chainId ?? 0, {
    enabled: needed
  });
  return {
    registry: registry.data,
    lookup: needed ? registry : null,
    namesMissing: needed && registry.isError && registry.data === undefined
  };
}
